/**
 * Утилиты для обработки текста сообщений
 * Единая логика фильтрации цитат и обрезки текста
 */

export interface ProcessMessageTextOptions {
  maxLength?: number;
  filterQuotes?: boolean;
  suffix?: string;
}

/**
 * Обрабатывает текст сообщения: фильтрует цитаты и ограничивает длину
 * @param text - исходный текст
 * @param options - опции обработки
 * @returns обработанный текст
 */
export const processMessageText = (text: string, options: ProcessMessageTextOptions = {}): string => {
  const { maxLength = 30, filterQuotes = true, suffix = '...' } = options;
  
  // Проверяем тип данных и конвертируем в строку
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  let processedText = text;
  
  // Фильтрация цитат
  if (filterQuotes) {
    const lines = text.split('\n');
    const filteredLines = lines.filter(line => {
      const trimmed = line.trim();
      return !trimmed.startsWith('>') && !trimmed.startsWith('&gt;');
    });
    
    // Берем только первую непустую строку для компактности
    const nonEmptyLines = filteredLines.filter(line => line.trim().length > 0);
    processedText = nonEmptyLines.length > 0 ? nonEmptyLines[0].trim() : '';
  }
  
  // Обрезка текста
  if (processedText.length > maxLength) {
    return processedText.substring(0, maxLength) + suffix;
  }
  
  return processedText;
};

/**
 * Стандартные лимиты для разных типов отображения
 */
export const TEXT_LIMITS = {
  SIDEBAR: 240,         // Сайдбар и список диалогов — визуальная обрезка через CSS
  REPLY_PREVIEW: 80,     // Превью ответа
  QUOTE: 100,            // Цитирование при ответе
  LOG: 50,               // Логирование
  NOTIFICATION: 100      // Push-уведомления
} as const;

/**
 * Обрабатывает текст для сайдбара
 */
export const processSidebarText = (text: string): string => 
  processMessageText(text, { maxLength: TEXT_LIMITS.SIDEBAR, filterQuotes: true });

/**
 * Фильтрует цитаты без сокращения текста
 * Показывает только ответное сообщение, убирая цитированные строки
 */
export const filterQuotesOnly = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  
  const lines = text.split('\n');
  const filteredLines = lines.filter(line => {
    const trimmed = line.trim();
    return !trimmed.startsWith('>') && !trimmed.startsWith('&gt;');
  });
  
  // Возвращаем все непустые строки (только ответное сообщение)
  const nonEmptyLines = filteredLines.filter(line => line.trim().length > 0);
  return nonEmptyLines.join(' ').trim();
};

/**
 * Показывает только ответное сообщение (текст после цитаты)
 * Используется для списка контактов, где нужно видеть только ответ
 */
export const getReplyOnly = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  
  const lines = text.split('\n');
  const replyLines = lines.filter(line => {
    const trimmed = line.trim();
    return !trimmed.startsWith('>') && !trimmed.startsWith('&gt;');
  });
  
  // Берем только строки ответа, объединяем через пробел
  const nonEmptyReplyLines = replyLines.filter(line => line.trim().length > 0);
  return nonEmptyReplyLines.join(' ').trim();
};

/**
 * Показывает только ответное сообщение с сокращением до 30 символов
 * Используется для списка контактов
 */
export const getReplyOnlyTruncated = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  
  const replyText = getReplyOnly(text);
  if (!replyText) return '';
  
  // Сокращаем до 30 символов
  if (replyText.length > TEXT_LIMITS.SIDEBAR) {
    return replyText.substring(0, TEXT_LIMITS.SIDEBAR) + '...';
  }
  
  return replyText;
};

/**
 * Показывает последнее сообщение с сокращением до 30 символов
 * Фильтрует цитаты, но показывает любой текст (не только ответы)
 */
export const getLastMessageTruncated = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  
  const lines = text.split('\n');
  const filteredLines = lines.filter(line => {
    const trimmed = line.trim();
    return !trimmed.startsWith('>') && !trimmed.startsWith('&gt;');
  });
  
  // Берем все непустые строки, объединяем через пробел
  const nonEmptyLines = filteredLines.filter(line => line.trim().length > 0);
  const messageText = nonEmptyLines.join(' ').trim();
  
  if (!messageText) return '';
  
  // Сокращаем до 30 символов
  if (messageText.length > TEXT_LIMITS.SIDEBAR) {
    return messageText.substring(0, TEXT_LIMITS.SIDEBAR) + '...';
  }
  
  return messageText;
};

/**
 * Извлекает текст цитаты из reply-сообщения
 * Например: "> [reply:123]Привет!" → "Привет!"
 * Или: "> Привет!" → "Привет!"
 */
export const extractQuotePreview = (text: string, maxLength: number = 60): string => {
  if (!text || typeof text !== 'string') return '';
  
  const lines = text.split('\n');
  const quoteLines: string[] = [];
  
  for (const line of lines) {
    if (line.startsWith('> ')) {
      // Убираем "> " и извлекаем текст
      let quoteText = line.substring(2);
      // Убираем [reply:ID] если есть
      quoteText = quoteText.replace(/^\[reply:\d+\]/, '');
      quoteLines.push(quoteText);
    } else if (quoteLines.length > 0) {
      // Первая не-цитата после цитат - заканчиваем сбор
      break;
    }
  }
  
  const result = quoteLines.join(' ').trim();
  if (!result) return '';
  
  // Обрезаем если нужно
  if (result.length > maxLength) {
    return result.substring(0, maxLength) + '...';
  }
  
  return result;
};

/**
 * Обрабатывает текст для превью ответа
 */
export const processReplyPreviewText = (text: string): string => 
  processMessageText(text, { maxLength: TEXT_LIMITS.REPLY_PREVIEW, filterQuotes: true });

/**
 * Обрабатывает текст для цитирования
 */
export const processQuoteText = (text: string): string => 
  processMessageText(text, { maxLength: TEXT_LIMITS.QUOTE, filterQuotes: false });

/**
 * Обрабатывает текст для логирования
 */
export const processLogText = (text: string): string => 
  processMessageText(text, { maxLength: TEXT_LIMITS.LOG, filterQuotes: false });

/**
 * Обрабатывает текст для уведомлений
 */
export const processNotificationText = (text: string): string => 
  processMessageText(text, { maxLength: TEXT_LIMITS.NOTIFICATION, filterQuotes: true });
